import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';

interface FantasyLeague {
  id: number;
  name: string;
  league_type: string;
  live_tournament_id: number;
  tournament_name: string;
  max_participants: number;
  current_participants: number;
  status: string;
  is_public: boolean;
  join_code?: string;
  created_by: number;
  created_at: string;
  is_creator: boolean;
}

const DraftLeaguePage: React.FC = () => {
  const [leagues, setLeagues] = useState<FantasyLeague[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuthStatus();
    fetchDraftLeagues();
  }, []);

  const checkAuthStatus = () => {
    const token = localStorage.getItem('authToken');
    setIsLoggedIn(!!token);
  };

  const fetchDraftLeagues = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await apiService.getFantasyLeagues();

      if (data.success) {
        const draftLeagues = data.tournaments.filter(
          (league: FantasyLeague) => league.league_type === 'draft_league'
        );
        setLeagues(draftLeagues);
      } else {
        setError(data.message || 'Failed to load leagues');
      }
    } catch (err) {
      setError('Failed to load leagues');
      console.error('Error fetching draft leagues:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLeague = () => {
    if (!isLoggedIn) {
      navigate('/tournaments');
      return;
    }
    navigate('/create-league?type=draft_league');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto py-8 px-4">
      {/* Header */}
      <div className="text-center mb-12">
        <div className="text-6xl mb-4">📋</div>
        <h1 className="text-4xl font-bold text-white mb-4">Draft League Tournaments</h1>
        <p className="text-xl text-white/80 max-w-3xl mx-auto">
          Experience the thrill of a snake draft! Take turns selecting players with your friends. 
          Once a player is drafted, they're off the board for everyone else.
        </p>
      </div>

      {/* How It Works */}
      <div className="bg-white/10 backdrop-blur-md rounded-lg p-8 border border-white/20 mb-8">
        <h2 className="text-2xl font-bold text-white mb-6">How Draft League Works</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="bg-orange-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">🎲</span>
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">1. Snake Draft</h3>
            <p className="text-white/70">Take turns picking players. The order reverses each round, ensuring fair distribution.</p>
          </div>
          <div className="text-center">
            <div className="bg-blue-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">👥</span>
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">2. Build Team</h3>
            <p className="text-white/70">Strategically select players to build a balanced team across all positions.</p>
          </div>
          <div className="text-center">
            <div className="bg-purple-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">🏆</span>
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">3. Compete</h3>
            <p className="text-white/70">Your drafted team competes against others based on real player performances.</p>
          </div>
        </div>
      </div>

      {/* Draft Process */}
      <div className="bg-white/10 backdrop-blur-md rounded-lg p-8 border border-white/20 mb-8">
        <h2 className="text-2xl font-bold text-white mb-6">Draft Process</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-semibold text-white mb-3">Draft Rules</h3>
            <ul className="space-y-2 text-white/80">
              <li>• <strong>Snake Draft:</strong> Order reverses each round (1,2,3,4,4,3,2,1)</li>
              <li>• <strong>Time Limits:</strong> 60 seconds per pick</li>
              <li>• <strong>Auto-Pick:</strong> System selects best available if time expires</li>
              <li>• <strong>Position Limits:</strong> Maximum players per position</li>
            </ul>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white mb-3">Team Structure</h3>
            <ul className="space-y-2 text-white/80">
              <li>• <strong>Batsmen:</strong> 4-6 players</li>
              <li>• <strong>Bowlers:</strong> 3-5 players</li>
              <li>• <strong>All-rounders:</strong> 2-4 players</li>
              <li>• <strong>Wicket-keeper:</strong> 1-2 players</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Strategy Tips */}
      <div className="bg-white/10 backdrop-blur-md rounded-lg p-8 border border-white/20 mb-8">
        <h2 className="text-2xl font-bold text-white mb-6">Draft Strategy Tips</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-semibold text-white mb-3">Early Rounds</h3>
            <ul className="space-y-2 text-white/80">
              <li>• <strong>Star Players:</strong> Pick proven performers first</li>
              <li>• <strong>All-rounders:</strong> High-value picks for flexibility</li>
              <li>• <strong>Wicket-keepers:</strong> Quality keepers are rare</li>
              <li>• <strong>Form Players:</strong> Consider recent performance</li>
            </ul>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white mb-3">Late Rounds</h3>
            <ul className="space-y-2 text-white/80">
              <li>• <strong>Emerging Talent:</strong> Young players with potential</li>
              <li>• <strong>Fixture Analysis:</strong> Players with favorable schedules</li>
              <li>• <strong>Backup Options:</strong> Cover for key positions</li>
              <li>• <strong>Differential Picks:</strong> Unique selections for advantage</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-8">
        <button
          onClick={handleCreateLeague}
          className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white px-4 sm:px-8 py-2 sm:py-3 rounded-lg text-sm sm:text-lg font-semibold transition-all transform hover:scale-105"
        >
          Create Draft League
        </button>
        <Link
          to="/tournaments"
          className="bg-white/10 hover:bg-white/20 text-white px-4 sm:px-8 py-2 sm:py-3 rounded-lg text-sm sm:text-lg font-semibold border border-white/20 transition-all text-center"
        >
          Browse All Tournaments
        </Link>
      </div>
    </div>
  );
};

export default DraftLeaguePage; 